import { useEffect, useState } from 'react';

import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { CatalogApi } from '@backstage/plugin-catalog-react';

export type PickerOption = {
  value: string;
  label: string;
  description?: string;
  entity?: Entity;
};

export async function fetchEntitiesForPicker(
  catalogApi: CatalogApi,
  filter: any,
  fields: string[],
) {
  const { items } = await catalogApi.getEntities({ filter, fields });
  return items;
}

export function useEntityPickerData(
  catalogApi: CatalogApi,
  filter: any,
  fields: string[],
) {
  const [value, setValue] = useState<{ options: PickerOption[] }>({
    options: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const items = await fetchEntitiesForPicker(catalogApi, filter, fields);
        const options: PickerOption[] = items.map(entity => {
          const ref = stringifyEntityRef(entity);
          const label = entity.metadata.title || entity.metadata.name;
          const description = entity.metadata.description;
          return { value: ref, label, description, entity };
        });
        if (isMounted) setValue({ options });
      } catch (e: any) {
        if (isMounted) setError(e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    run();
    return () => {
      isMounted = false;
    };
  }, [catalogApi, filter, fields]);

  return { value, loading, error };
}

/**
 * Hook to fetch cloud-account Resources owned by a Product Area or its child teams
 * 
 * @param catalogApi - Catalog API instance
 * @param productAreaRef - Entity reference string for the Product Area (e.g., "group:default/carrier-digital-dpx-pe")
 * @returns Object containing cloudAccounts (Entity array), loading state, and optional error
 * 
 * @example
 * ```typescript
 * const productAreaRef = formContext?.formData?.product_area as string;
 * const { cloudAccounts, loading, error } = useCloudAccountsByProductArea(
 *   catalogApi,
 *   productAreaRef
 * );
 * ```
 */
export function useCloudAccountsByProductArea(
  catalogApi: CatalogApi,
  productAreaRef: string | undefined,
) {
  const [cloudAccounts, setCloudAccounts] = useState<Entity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;

    const fetchCloudAccounts = async () => {
      // If no product area is selected, return empty array (not an error state)
      if (!productAreaRef) {
        if (isMounted) {
          setCloudAccounts([]);
          setLoading(false);
          setError(undefined);
        }
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        // Step 1: Parse the productAreaRef to extract the group name
        const groupRef = parseEntityRef(productAreaRef);
        const productAreaName = groupRef.name;

        // Step 2: Fetch the Product Area Group entity to get its children
        const { items: productAreaGroups } = await catalogApi.getEntities({
          filter: {
            kind: 'Group',
            'metadata.name': productAreaName,
          },
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'spec.type',
            'spec.children',
          ],
        });

        if (productAreaGroups.length === 0) {
          throw new Error(`Product Area "${productAreaName}" not found`);
        }

        const productAreaGroup = productAreaGroups[0];

        // Step 3: Build list of valid owners (Product Area + all child teams)
        const validOwners = new Set<string>();
        validOwners.add(productAreaName);

        // Add all child team names to the valid owners set
        const children = (productAreaGroup.spec?.children as string[]) || [];
        children.forEach(child => validOwners.add(child));

        // Step 4: Fetch all cloud-account Resources
        const { items: allCloudAccounts } = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': 'cloud-account',
          },
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'metadata.title',
            'metadata.description',
            'metadata.annotations',
            'spec.type',
            'spec.owner',
            'spec.lifecycle',
            'spec.system',
          ],
        });

        // Step 5: Filter Resources where spec.owner matches any name in the valid owners list
        const filteredCloudAccounts = allCloudAccounts.filter(resource => {
          if (!resource.spec?.owner) {
            return false;
          }

          const owner = resource.spec.owner as string;

          // Handle both plain names and "group:" prefixed refs
          if (owner.startsWith('group:')) {
            try {
              const ownerRef = parseEntityRef(owner);
              const ownerName = ownerRef.name;
              return validOwners.has(ownerName);
            } catch {
              // If parsing fails, skip this resource
              return false;
            }
          } else {
            // Plain name format
            return validOwners.has(owner);
          }
        });

        if (isMounted) {
          setCloudAccounts(filteredCloudAccounts);
        }
      } catch (e: any) {
        if (isMounted) {
          setError(
            e instanceof Error
              ? e
              : new Error(`Failed to fetch cloud accounts: ${e}`),
          );
          setCloudAccounts([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCloudAccounts();

    return () => {
      isMounted = false;
    };
  }, [catalogApi, productAreaRef]);

  return { cloudAccounts, loading, error };
}
